if (!customElements.get('search-bar')) {
  const MAX_RESULTS = 12
  const COLOR_SEARCH_TERMS = [
    'black',
    'white',
    'gray',
    'heather',
    'charcoal',
    'silver',
    'carbon',
    'shadow',
    'cream',
    'ivory',
    'beige',
    'tan',
    'khaki',
    'oatmeal',
    'brown',
    'chocolate',
    'camel',
    'navy',
    'blue',
    'cobalt',
    'indigo',
    'marine',
    'teal',
    'turquoise',
    'icicle',
    'green',
    'olive',
    'sage',
    'sagewood',
    'mint',
    'red',
    'burgundy',
    'maroon',
    'wine',
    'ashberry',
    'pink',
    'blush',
    'rose',
    'purple',
    'lavender',
    'plum',
    'orange',
    'rust',
    'coral',
    'peach',
    'yellow',
    'mustard',
    'gold',
    'forge',
    'metallic',
    'multicolor',
    'rainbow',
    'neon',
  ]

  class SearchBar extends HTMLElement {
    searchTimeout = null
    abortController = new AbortController()

    constructor() {
      super()
      this.searchForm = this.querySelector('form[role="search"]')
      this.searchInput = this.querySelector('#searchbar__input')
      this.searchTypeInput = this.querySelector('.searchbar__type-input')
      this.searchResultsContainer = this.querySelector('#searchResults')
      this.closeSearchButton = this.querySelector('#nav__search-close')
      this.clearButton = this.querySelector('.searchbar__clear-button')
      this.shortcutButtons = this.querySelectorAll('[data-search-shortcut]')
    }

    connectedCallback() {
      this.searchInput.addEventListener('input', this.handleSearchInput.bind(this))
      this.searchForm?.addEventListener('submit', this.handleSearchSubmit.bind(this))
      this.closeSearchButton.addEventListener('click', this.closeSearch.bind(this))
      this.clearButton?.addEventListener('click', this.clearSearchInput.bind(this))

      for (const button of this.shortcutButtons) {
        button.addEventListener('click', this.handleShortcutClick.bind(this))
      }

      // Add keydown event listener for Escape key
      document.addEventListener('keydown', this.handleKeydown.bind(this))

      // Add click event listener for clicks outside search bar
      document.addEventListener('click', this.handleClickOutside.bind(this))
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown.bind(this))
      document.removeEventListener('click', this.handleClickOutside.bind(this))
    }

    openSearch() {
      this.classList.add('active')
      const topOffset = Math.max(0, this.getBoundingClientRect().top)
      this.style.setProperty('--search-bar-top-offset', `${topOffset}px`)
      this.setAttribute('aria-hidden', 'false')
      this.removeAttribute('inert')
      this.searchInput.value = ''
      this.clearButton?.classList.add('hidden')
      this.updateActiveShortcut('')
      this.updateSearchType()
      this.searchInput.focus()
      document.body.classList.add('overflow-hidden')
    }

    closeSearch() {
      const navWrapper = document.querySelector('.shopify-section.main-nav__wrapper')
      const announcementBar = document.querySelector(
        '.shopify-section.new-announcement-bar__wrapper',
      )
      navWrapper.classList.add('is-sticky')
      announcementBar.classList.add('is-sticky')
      this.classList.remove('active')
      this.setAttribute('aria-hidden', 'true')
      this.setAttribute('inert', '')
      this.clearSearchResults()
      document.body.classList.remove('overflow-hidden')
    }

    clearSearchInput({ focus = true } = {}) {
      this.searchInput.value = ''
      // Reuse the input flow to hide the button and clear results
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }))
      if (focus) this.searchInput.focus()
    }

    handleSearchInput(event) {
      const query = event.target.value.trim()

      this.clearButton?.classList.toggle('hidden', query.length === 0)
      this.updateSearchType()

      this.abortController.abort()
      this.abortController = new AbortController()

      // Clear previous timeouts
      clearTimeout(this.searchTimeout)
      clearTimeout(this.predictiveDisplayTimeout)

      if (query.length === 0) {
        this.clearSearchResults()
        this.updateActiveShortcut(query)
        return
      }

      this.updateActiveShortcut(query)

      // Show skeleton immediately for perceived speed while results load
      this.showLoadingSkeleton()

      // Debounce the search to avoid too many requests
      this.searchTimeout = setTimeout(() => {
        this.runHybridSearch(query)
      }, 300)
    }

    handleShortcutClick(event) {
      this.searchInput.value = event.currentTarget.dataset.term
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }))
      this.searchInput.focus()

      if (typeof this.scrollToSearchResults === 'function') {
        requestAnimationFrame(this.scrollToSearchResults.bind(this))
      }
    }

    updateActiveShortcut(query) {
      const queryLower = query.toLowerCase()

      for (const button of this.shortcutButtons) {
        const isActive = queryLower.length > 0 && button.dataset.term.toLowerCase() === queryLower
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', String(isActive))
      }
    }

    // Hybrid search: fast predictive (prefix) results fill the dropdown first,
    // then slower semantic results replace them when they arrive.
    // Semantic results only win when they actually contain items, so partial
    // queries ('r', 're') keep showing predictive prefix matches.
    async runHybridSearch(query) {
      let semanticShown = false
      let predictiveShown = false

      const predictivePromise = this.fetchPredictiveSearchSection(query)

      predictivePromise.then((html) => {
        if (html && html !== 'aborted') this.logSearchResultCount('predictive', html, query)
        if (semanticShown || html === 'aborted' || !html) return
        // Keep the skeleton up instead of flashing 'no results' before semantic results land
        if (!this.hasResultItems(html)) return
        // Grace period: skip showing predictive results entirely if semantic
        // results arrive right behind them, preventing a brief flash
        this.predictiveDisplayTimeout = setTimeout(() => {
          if (semanticShown) return
          predictiveShown = true
          this.displaySearchResults(html, 'predictive-search')
        }, 200)
      })

      const semanticHTML = await this.fetchSemanticSearchSection(query)
      clearTimeout(this.predictiveDisplayTimeout)
      // Aborted request means a newer one is in flight, keep current content up
      if (semanticHTML === 'aborted') return

      if (!semanticHTML) {
        // Semantic fetch failed: keep predictive results if shown, clear a stuck skeleton
        if (this.searchResultsContainer.querySelector('.predictive-search__item--skeleton'))
          this.clearSearchResults()
        return
      }

      this.logSearchResultCount('semantic', semanticHTML, query)

      if (this.hasResultItems(semanticHTML)) {
        semanticShown = true
        this.displaySearchResults(semanticHTML, 'search-dropdown')
        return
      }

      // Semantic found nothing (common for partial words): fall back to predictive
      const predictiveHTML = await predictivePromise
      if (predictiveHTML !== 'aborted' && predictiveHTML && this.hasResultItems(predictiveHTML)) {
        if (!predictiveShown) this.displaySearchResults(predictiveHTML, 'predictive-search')
        return
      }

      // Neither engine found anything: show the semantic no-results message
      semanticShown = true
      this.displaySearchResults(semanticHTML, 'search-dropdown')
    }

    hasResultItems(html) {
      return html.includes('data-search-result-item')
    }

    countSearchResultItems(html) {
      const parsedDoc = new DOMParser().parseFromString(html, 'text/html')
      return parsedDoc.querySelectorAll('[data-search-result-item]').length
    }

    logSearchResultCount(searchType, html, query) {
      const count = this.countSearchResultItems(html)
      console.log(`[search-bar] ${searchType} search for "${query}": ${count} results`)
    }

    showLoadingSkeleton() {
      // Skeleton already visible, keep its shimmer animation running
      if (this.searchResultsContainer.querySelector('.predictive-search__item--skeleton')) return

      const skeletonItem = `
        <li class="predictive-search__item predictive-search__item--skeleton">
          <div class="predictive-search__link">
            <div class="loading__skeleton predictive-search__skeleton-image"></div>
            <div class="predictive-search__item-content">
              <div class="loading__skeleton predictive-search__skeleton-title"></div>
              <div class="loading__skeleton predictive-search__skeleton-price"></div>
            </div>
          </div>
        </li>`

      const showSkeleton = () => {
        this.searchResultsContainer.innerHTML = `
          <div class="predictive-search-results-container">
            <div class="predictive-search-results">
              <p class="predictive-search__results-heading">
                <span class="loading__skeleton predictive-search__skeleton-heading"></span>
              </p>
              <div class="predictive-search__list-container">
                <ul class="predictive-search__list">${skeletonItem.repeat(8)}</ul>
              </div>
            </div>
          </div>`
      }

      // Use View Transitions API if supported, otherwise fallback
      document.startViewTransition?.(showSkeleton) ?? showSkeleton()
    }

    handleSearchSubmit() {
      this.updateSearchType()
    }

    updateSearchType() {
      if (!this.searchTypeInput) return

      this.searchTypeInput.value = 'product'
    }

    hasColorSearchTerm(query) {
      // Color searches should open the exact product instead of the product
      // family's default pack. Keep in sync with sections/search-dropdown.liquid.
      const queryLower = query.toLowerCase()

      return COLOR_SEARCH_TERMS.some((term) => queryLower.includes(term))
    }

    fetchPredictiveSearchSection(query) {
      // Predictive search: fast keyword/prefix matching, e.g. 're' -> 'red'
      // resources[options][fields] adds 'tag' to the default searched fields
      const searchFields = 'title,product_type,variants.title,vendor,tag'
      const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[options][fields]=${searchFields}&section_id=predictive-search`
      return this.fetchSectionHTML(url)
    }

    fetchSemanticSearchSection(query) {
      // Section-render the regular /search route (not /search/suggest) so the
      // dropdown gets Shopify semantic search results, e.g. 'red' -> maroon
      const url = `${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last&section_id=search-dropdown`
      return this.fetchSectionHTML(url)
    }

    async fetchSectionHTML(url) {
      try {
        const response = await fetch(url, { signal: this.abortController.signal })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const html = await response.text()
        return html
      } catch (error) {
        if (error.name === 'AbortError') return 'aborted'
        console.error('Error fetching search section:', error)
        return null
      }
    }

    displaySearchResults(resultsHTML, sectionId) {
      if (!resultsHTML) {
        this.clearSearchResults()
        return
      }

      // Swap skeleton/old results for new results in a single DOM update
      // to avoid a flash of empty dropdown between transitions
      const updateResults = () => {
        const parsedDoc = new DOMParser().parseFromString(resultsHTML, 'text/html')
        const section = parsedDoc.querySelector(`#shopify-section-${sectionId}`)

        if (!section) {
          this.searchResultsContainer.innerHTML = ''
          return
        }

        // Filter hidden products, then cap the navbar overlay to two desktop rows.
        const productItems = section.querySelectorAll('[data-search-result-item]')
        productItems.forEach((item) => {
          const tags = item.dataset.tags || ''
          if (tags.toLowerCase().includes('hidden from search')) {
            item.remove()
          }
        })

        const visibleProductItems = section.querySelectorAll('[data-search-result-item]')
        visibleProductItems.forEach((item, index) => {
          if (index >= MAX_RESULTS) item.remove()
        })

        const resultCount = section.querySelectorAll('[data-search-result-item]').length
        const heading = section.querySelector('.predictive-search__results-heading')
        if (heading && resultCount > 0) {
          const resultWord = resultCount === 1 ? 'result' : 'results'
          const query = document.createElement('strong')
          query.textContent = this.searchInput.value.trim()
          heading.replaceChildren(
            document.createTextNode(`${resultCount} ${resultWord} for "`),
            query,
            document.createTextNode('"'),
          )
        }

        this.searchResultsContainer.innerHTML = section.innerHTML
        window.igEvents = window.igEvents || [];
        window.igEvents.push({"event": "Search Results - Menu"});
      }

      // Use View Transitions API if supported, otherwise fallback
      document.startViewTransition?.(updateResults) ?? updateResults()
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('active')) {
        this.closeSearch()
        document.querySelector('#nav__tab--search').focus()
      }
    }

    handleClickOutside(event) {
      const mainNav = document.querySelector('main-nav')
      const isSearchActive = this.classList.contains('active')
      const clickedBackdrop = event.target === this
      const clickedOutsideSearch =
        !this.contains(event.target) && !mainNav?.contains(event.target)

      if (isSearchActive && (clickedBackdrop || clickedOutsideSearch)) this.closeSearch()
    }

    clearSearchResults() {
      const clearContainer = () => {
        this.searchResultsContainer.innerHTML = ''
      }

      // Use View Transitions API if supported, otherwise fallback
      document.startViewTransition?.(clearContainer) ?? clearContainer()
    }
  }

  customElements.define('search-bar', SearchBar)
}
